import React, { useState, Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, CheckCircle2, ShieldCheck, XCircle, Gift, Star, Users, BookOpen, Award, Clock, ChevronDown, Lock, Zap, Download } from 'lucide-react';
import { CountdownTimerDark } from '../components/CountdownTimerDark';
import { SocialProofPopup } from '../components/SocialProofPopup';
import { StickyMobileCTA } from '../components/StickyMobileCTA';
import { TestimonialCardDark } from '../components/TestimonialCardDark';
import { MetaPixel } from '../components/MetaPixel';
import { useLocalCurrency } from '../hooks/useLocalCurrency';
const CHECKOUT_URL = 'https://pay.hotmart.com/V104120573X?checkoutMode=10';
const PRODUCT_IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/a4xuApciDBZeojPST7sKJv/cover_1.png',
  bono1: 'https://cdn.magicpatterns.com/uploads/2dVQ56F8EfQrQ5in82JWcf/Cover_bono_1.png',
  bono2: 'https://cdn.magicpatterns.com/uploads/1p6tpY235ANNL8E643Hhv7/cover_bono_2.png',
  bono3: 'https://cdn.magicpatterns.com/uploads/wHomnvFnvrby4gjNXSHQd6/cover_bono_3.png',
  bono4: 'https://cdn.magicpatterns.com/uploads/1QFP8c3BRga8vfLEHvf4Gu/cover_bono_4.png'
};
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.5
  }
};
const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  },
  viewport: {
    once: true
  }
};
const staggerItem = {
  initial: {
    opacity: 0,
    x: -20
  },
  whileInView: {
    opacity: 1,
    x: 0
  },
  transition: {
    duration: 0.4
  }
};
function CTAButton({
  scrollToSection = true,
  size = 'lg',
  children
}: {
  scrollToSection?: boolean;
  size?: 'md' | 'lg' | 'xl';
  children: React.ReactNode;
}) {
  const handleClick = () => {
    if (scrollToSection) {
      document.getElementById('purchase')?.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      window.location.href = CHECKOUT_URL;
    }
  };
  const sizeClasses = {
    md: 'py-3 px-6 text-base',
    lg: 'py-4 px-8 text-lg',
    xl: 'py-5 px-10 text-xl'
  };
  return <motion.button onClick={handleClick} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={`bg-orange-500 hover:bg-orange-400 text-white border-2 border-orange-600 shadow-[0_4px_0_rgb(154,52,18)] active:shadow-none active:translate-y-[4px] rounded-full uppercase tracking-wide font-bold flex items-center justify-center gap-2 transition-all ${sizeClasses[size]}`} data-id="element-268">
      {children}
      <ArrowRight className="w-5 h-5" data-id="element-269" />
    </motion.button>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-slate-800 rounded-xl overflow-hidden" data-id="element-270">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-5 text-left bg-slate-900 hover:bg-slate-800/50 transition-colors" data-id="element-271">
        <span className="font-bold text-white pr-4" data-id="element-272">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-273">
          <ChevronDown className="w-5 h-5 text-emerald-400 flex-shrink-0" data-id="element-274" />
        </motion.div>
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} transition={{
      duration: 0.3
    }} className="overflow-hidden" data-id="element-275">
        <p className="p-5 pt-0 text-slate-400 leading-relaxed" data-id="element-276">{answer}</p>
      </motion.div>
    </div>;
}
export function SalesPageDark() {
  const {
    flag,
    currency,
    symbol,
    localPrice,
    localPriceStrikethrough,
    isLoading,
    isUSD,
    country
  } = useLocalCurrency();
  return <div className="min-h-screen bg-slate-950 text-white antialiased selection:bg-emerald-500/30 pt-12" data-id="element-277">
      <MetaPixel data-id="element-278" />
      <CountdownTimerDark data-id="element-279" />
      <SocialProofPopup data-id="element-280" />
      <StickyMobileCTA data-id="element-281" />

      {/* ═══ SECTION 1: HERO ═══ */}
      <header className="bg-slate-950 py-16 md:py-24 px-4 relative overflow-hidden" data-id="element-282">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent" data-id="element-283" />
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-284">
          <motion.div {...fadeInUp} className="text-center mb-12" data-id="element-285">
            <span className="inline-block bg-emerald-500/20 text-emerald-400 font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider border border-emerald-500/30 mb-6" data-id="element-286">
              📘 Para Docentes de Matemática
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-extrabold mb-6 leading-tight" data-id="element-287">
              ORGANIZA TODO TU AÑO DE{' '}
              <span className="text-emerald-400" data-id="element-288">MATEMÁTICA SIN ESTRÉS</span>
            </h1>
            <p className="text-xl md:text-2xl text-emerald-400 font-bold mb-4" data-id="element-289">
              5 TOMOS COMPLETOS + 4 BONOS EXCLUSIVOS
            </p>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto" data-id="element-290">
              El sistema completo para docentes de Primaria y Secundaria que
              quieren dejar de improvisar y tener todo el año resuelto.
            </p>
          </motion.div>

          {/* Fan composition */}
          <motion.div {...fadeInUp} className="relative flex justify-center items-center mb-12 h-[300px] md:h-[400px]" data-id="element-291">
            <div className="absolute w-64 h-64 md:w-80 md:h-80 bg-emerald-500/20 rounded-full blur-3xl" data-id="element-292" />
            {[-12, -6, 0, 6, 12].map((rotation, i) => <motion.img key={i} src={PRODUCT_IMAGES.main} alt="MegaPack Matemática" width={200} height={280} className="absolute w-32 md:w-48 drop-shadow-2xl" style={{
            transform: `rotate(${rotation}deg) translateX(${(i - 2) * 20}px)`,
            zIndex: 5 - Math.abs(i - 2)
          }} initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1,
            duration: 0.5
          }} data-id="element-293" />)}
          </motion.div>

          {/* Stats box */}
          <motion.div {...fadeInUp} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-xl mx-auto mb-8" data-id="element-294">
            <p className="text-center text-slate-300 mb-2" data-id="element-295">Más de</p>
            <p className="text-center text-5xl font-black text-emerald-400 mb-2" data-id="element-296">
              +2,500
            </p>
            <p className="text-center text-slate-300" data-id="element-297">
              docentes ya organizaron su año escolar
            </p>
          </motion.div>

          {/* Feature pills */}
          <motion.div {...fadeInUp} className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-298">
            {[{
            icon: BookOpen,
            text: 'Material 100% Editable'
          }, {
            icon: Download,
            text: 'Descarga Inmediata'
          }, {
            icon: Zap,
            text: 'Listo para Imprimir'
          }].map((item, i) => <span key={i} className="flex items-center gap-2 bg-slate-900 border border-slate-800 px-4 py-2 rounded-full text-sm" data-id="element-299">
                <item.icon className="w-4 h-4 text-emerald-400" data-id="element-300" />
                <span className="text-slate-300" data-id="element-301">{item.text}</span>
              </span>)}
          </motion.div>

          {/* CTA */}
          <motion.div {...fadeInUp} className="flex flex-col items-center gap-6" data-id="element-302">
            <CTAButton size="xl" data-id="element-303">Quiero Acceso Ahora</CTAButton>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-400" data-id="element-304">
              <span className="flex items-center gap-2" data-id="element-305">
                <ShieldCheck className="w-4 h-4 text-emerald-400" data-id="element-306" />
                Garantía 30 días
              </span>
              <span className="flex items-center gap-2" data-id="element-307">
                <Lock className="w-4 h-4 text-emerald-400" data-id="element-308" />
                Pago 100% seguro
              </span>
              <span className="flex items-center gap-2" data-id="element-309">
                <Zap className="w-4 h-4 text-emerald-400" data-id="element-310" />
                Acceso inmediato
              </span>
            </div>
          </motion.div>
        </div>
      </header>

      {/* ═══ SECTION 2: SOCIAL PROOF BAR ═══ */}
      <section className="bg-slate-900 border-y border-slate-800 py-12 px-4" data-id="element-311">
        <div className="max-w-6xl mx-auto" data-id="element-312">
          <div className="grid md:grid-cols-3 gap-8 mb-12" data-id="element-313">
            {[{
            value: '+2,500',
            label: 'Docentes activos'
          }, {
            value: '5 Tomos',
            label: 'Material completo'
          }, {
            value: '4.9/5',
            label: 'Calificación promedio'
          }].map((stat, i) => <motion.div key={i} {...fadeInUp} className="text-center" data-id="element-314">
                <p className="text-4xl font-black text-emerald-400 mb-1" data-id="element-315">
                  {stat.value}
                </p>
                <p className="text-slate-400" data-id="element-316">{stat.label}</p>
              </motion.div>)}
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-317">
            {[{
            text: '"Recuperé mis fines de semana"',
            name: 'Laura M.'
          }, {
            text: '"Mis alumnos entienden mejor"',
            name: 'Carlos R.'
          }, {
            text: '"Vale cada centavo"',
            name: 'Ana T.'
          }].map((quote, i) => <motion.div key={i} {...fadeInUp} className="bg-slate-950 p-4 rounded-xl border border-slate-800" data-id="element-318">
                <div className="flex gap-1 mb-2" data-id="element-319">
                  {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" data-id="element-320" />)}
                </div>
                <p className="text-slate-300 text-sm italic mb-2" data-id="element-321">
                  {quote.text}
                </p>
                <p className="text-slate-500 text-xs" data-id="element-322">— {quote.name}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 3: PROBLEM ═══ */}
      <section className="bg-slate-950 py-20 px-4" data-id="element-323">
        <div className="max-w-4xl mx-auto" data-id="element-324">
          <motion.h2 {...fadeInUp} className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-325">
            ¿Te suena familiar alguna de estas situaciones?
          </motion.h2>

          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="space-y-4" data-id="element-326">
            {['Llegas cansado/a y no sabes qué clase preparar para mañana', 'Pasas horas buscando ejercicios y nunca encuentras lo que necesitas', 'Tus alumnos se frustran porque el material no es adecuado', 'Improvisas más de lo que planificas', 'Sientes que el año escolar te supera constantemente'].map((pain, i) => <motion.div key={i} variants={staggerItem} className="bg-slate-900 p-5 rounded-xl border border-slate-800 flex items-start gap-4" data-id="element-327">
                <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-328" />
                <p className="text-slate-300" data-id="element-329">{pain}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 4: EMOTIONAL TRANSITION ═══ */}
      <section className="bg-slate-900 py-16 px-4 border-y border-slate-800" data-id="element-330">
        <div className="max-w-3xl mx-auto text-center" data-id="element-331">
          <motion.h2 {...fadeInUp} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-332">
            ¿Y si pudieras tener{' '}
            <span className="text-orange-400 underline decoration-orange-400/50 underline-offset-4" data-id="element-333">
              todo el año resuelto
            </span>{' '}
            en un solo lugar?
          </motion.h2>
          <motion.p {...fadeInUp} className="text-slate-400 text-lg mb-8" data-id="element-334">
            Imagina llegar cada día sabiendo exactamente qué enseñar, con
            material listo para imprimir y actividades que realmente funcionan.
          </motion.p>
          <motion.div {...fadeInUp} data-id="element-335">
            <CTAButton size="lg" data-id="element-336">Descubre Cómo</CTAButton>
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 5: PRODUCT PRESENTATION ═══ */}
      <section className="bg-slate-950 py-20 px-4" data-id="element-337">
        <div className="max-w-6xl mx-auto" data-id="element-338">
          <motion.div {...fadeInUp} className="text-center mb-16" data-id="element-339">
            <span className="inline-block bg-emerald-500/20 text-emerald-400 font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider border border-emerald-500/30 mb-6" data-id="element-340">
              La Solución Definitiva
            </span>
            <h2 className="text-4xl md:text-6xl font-black mb-4" data-id="element-341">
              MEGAPACK{' '}
              <span className="text-emerald-400" data-id="element-342">MATEMÁTICA LISTA™</span>
            </h2>
            <p className="text-xl text-emerald-400 font-bold mb-4" data-id="element-343">
              5 Tomos Completos para Todo el Año Escolar
            </p>
            <p className="text-slate-300 max-w-2xl mx-auto" data-id="element-344">
              Un sistema anual completo de clases listas para usar. No es un PDF
              suelto. Es todo lo que necesitas para enseñar matemática con
              claridad y estructura.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-345">
            <motion.div {...fadeInUp} data-id="element-346">
              <img src={PRODUCT_IMAGES.main} alt="MegaPack Matemática Lista" className="w-full max-w-md mx-auto drop-shadow-2xl" loading="lazy" decoding="async" data-id="element-347" />
            </motion.div>
            <motion.div {...fadeInUp} className="space-y-4" data-id="element-348">
              {['Planificación anual, mensual y semanal lista', 'Secuencias didácticas para cada unidad', 'Cientos de ejercicios y problemas guiados', 'Evaluaciones con rúbricas editables'].map((feature, i) => <div key={i} className="flex items-center gap-3 bg-slate-900 p-4 rounded-xl border border-slate-800" data-id="element-349">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" data-id="element-350" />
                  <span className="text-slate-300" data-id="element-351">{feature}</span>
                </div>)}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 6: WHAT'S INCLUDED ═══ */}
      <section className="bg-slate-900 py-20 px-4 border-y border-slate-800" data-id="element-352">
        <div className="max-w-5xl mx-auto" data-id="element-353">
          <motion.h2 {...fadeInUp} className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-354">
            Todo lo que incluye el MegaPack
          </motion.h2>

          <motion.div {...fadeInUp} className="bg-slate-950 rounded-2xl border border-slate-800 p-8" data-id="element-355">
            <div className="grid md:grid-cols-2 gap-6" data-id="element-356">
              {[{
              title: 'Planificación Total',
              desc: 'Anual, mensual y semanal con registro de avance'
            }, {
              title: 'Unidades Organizadas',
              desc: 'Numeración, Operaciones, Fracciones, Geometría, Medidas'
            }, {
              title: 'Actividades Listas',
              desc: 'Fichas imprimibles y ejercicios guiados'
            }, {
              title: 'Problemas con Estrategia',
              desc: 'Resolución paso a paso para cada tema'
            }, {
              title: 'Proyectos Aplicados',
              desc: 'Presupuesto, geometría, encuestas, lógica'
            }, {
              title: 'Sistema de Evaluación',
              desc: 'Pruebas por unidad, exit tickets, rúbricas'
            }].map((item, i) => <div key={i} className="flex items-start gap-4" data-id="element-357">
                  <Check className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-1" data-id="element-358" />
                  <div data-id="element-359">
                    <p className="font-bold text-white" data-id="element-360">{item.title}</p>
                    <p className="text-slate-400 text-sm" data-id="element-361">{item.desc}</p>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 7: BONUSES ═══ */}
      <section className="bg-slate-950 py-20 px-4" data-id="element-362">
        <div className="max-w-5xl mx-auto" data-id="element-363">
          <motion.div {...fadeInUp} className="text-center mb-12" data-id="element-364">
            <span className="inline-block bg-orange-500/20 text-orange-400 font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider border border-orange-500/30 mb-4 animate-pulse" data-id="element-365">
              🎁 Gratis Hoy
            </span>
            <h2 className="text-3xl md:text-4xl font-bold" data-id="element-366">
              4 Bonos Exclusivos Incluidos
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-367">
            {[{
            title: 'Evaluaciones Premium',
            desc: '60+ pruebas con versiones A/B y claves',
            img: PRODUCT_IMAGES.bono1
          }, {
            title: 'Banco de Juegos',
            desc: '30+ actividades lúdicas para enseñar',
            img: PRODUCT_IMAGES.bono2
          }, {
            title: 'Kit Visual Matemática',
            desc: 'Láminas de estrategias y resolución',
            img: PRODUCT_IMAGES.bono3
          }, {
            title: 'Planificación Perfecta',
            desc: 'Calendario anual 100% editable',
            img: PRODUCT_IMAGES.bono4
          }].map((bono, i) => <motion.div key={i} {...fadeInUp} className="bg-slate-900 rounded-2xl border border-slate-800 p-6 hover:border-emerald-500/30 transition-colors group" data-id="element-368">
                <div className="flex items-start gap-4" data-id="element-369">
                  <Gift className="w-8 h-8 text-orange-400 flex-shrink-0" data-id="element-370" />
                  <div className="flex-1" data-id="element-371">
                    <h3 className="font-bold text-white text-lg mb-1" data-id="element-372">
                      {bono.title}
                    </h3>
                    <p className="text-slate-400 text-sm" data-id="element-373">{bono.desc}</p>
                  </div>
                </div>
                <img src={bono.img} alt={bono.title} className="w-full h-48 object-contain mt-4 group-hover:scale-105 transition-transform" loading="lazy" decoding="async" data-id="element-374" />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 8: URGENCY ═══ */}
      <section className="bg-slate-900 py-16 px-4 border-y border-slate-800" data-id="element-375">
        <div className="max-w-3xl mx-auto text-center" data-id="element-376">
          <motion.div {...fadeInUp} data-id="element-377">
            <p className="text-slate-400 mb-4" data-id="element-378">
              Esta oferta especial termina en:
            </p>
            <div className="text-6xl md:text-8xl font-mono font-black text-orange-400 mb-6 drop-shadow-[0_0_30px_rgba(251,146,60,0.3)]" data-id="element-379">
              14:59
            </div>
            <p className="text-slate-300 mb-8" data-id="element-380">
              Después de este tiempo, el precio volverá a{' '}
              <span className="text-white font-bold" data-id="element-381">
                {isUSD ? '$47 USD' : `${symbol}${localPriceStrikethrough} ${currency}`}
              </span>
            </p>
            <CTAButton size="xl" data-id="element-382">Asegurar Mi Acceso Ahora</CTAButton>
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 9: PRICING ═══ */}
      <section id="purchase" className="bg-slate-950 py-20 px-4" data-id="element-383">
        <div className="max-w-4xl mx-auto" data-id="element-384">
          <motion.div {...fadeInUp} className="text-center mb-8" data-id="element-385">
            <span className="inline-block bg-emerald-500 text-white font-bold px-6 py-2 rounded-full text-sm uppercase tracking-wider animate-pulse mb-4" data-id="element-386">
              Hoy acceso completo por solo...
            </span>
          </motion.div>

          <motion.div {...fadeInUp} className="bg-white rounded-3xl border-4 border-emerald-500 overflow-hidden shadow-2xl shadow-emerald-500/20" data-id="element-387">
            <div className="p-8 md:p-12" data-id="element-388">
              <div className="flex flex-col md:flex-row items-center gap-8 mb-8" data-id="element-389">
                <img src={PRODUCT_IMAGES.main} alt="MegaPack" className="w-48 drop-shadow-xl" data-id="element-390" />
                <div className="flex-1 text-center md:text-left" data-id="element-391">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-392">
                    MegaPack Matemática Lista™
                  </h3>

                  {/* Value stack */}
                  <div className="space-y-2 mb-6 text-sm text-slate-600" data-id="element-393">
                    <p data-id="element-394">
                      <span className="line-through" data-id="element-395">5 Tomos Completos</span>{' '}
                      <span className="text-slate-400" data-id="element-396">valor $97</span>
                    </p>
                    <p data-id="element-397">
                      <span className="line-through" data-id="element-398">4 Bonos Exclusivos</span>{' '}
                      <span className="text-slate-400" data-id="element-399">valor $88</span>
                    </p>
                    <p data-id="element-400">
                      <span className="line-through" data-id="element-401">
                        Actualizaciones de por vida
                      </span>{' '}
                      <span className="text-slate-400" data-id="element-402">valor $47</span>
                    </p>
                  </div>

                  {/* Country flag + currency indicator */}
                  {!isLoading && !isUSD && <div className="bg-emerald-50 rounded-lg p-3 mb-4 text-sm border border-emerald-200 flex items-center gap-2" data-id="element-403">
                      <span className="text-2xl" data-id="element-404">{flag}</span>
                      <div data-id="element-405">
                        <p className="text-slate-800 font-bold" data-id="element-406">
                          Precio especial para {country}
                        </p>
                        <p className="text-slate-500 text-xs" data-id="element-407">
                          Paga en {currency} · Acceso inmediato
                        </p>
                      </div>
                    </div>}

                  {/* Strikethrough price */}
                  <div className="flex items-center justify-center md:justify-start gap-4 mb-2" data-id="element-408">
                    <span className="text-2xl text-slate-400 line-through" data-id="element-409">
                      {symbol}
                      {localPriceStrikethrough}
                    </span>
                    <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-bold" data-id="element-410">
                      70% OFF
                    </span>
                  </div>

                  {/* Main price - LOCAL CURRENCY with flag */}
                  <div className="flex items-center justify-center md:justify-start gap-3 mb-1" data-id="element-411">
                    {!isUSD && <span className="text-4xl" data-id="element-412">{flag}</span>}
                    <p className="text-6xl font-black text-slate-900" data-id="element-413">
                      {symbol}
                      {localPrice}
                    </p>
                  </div>
                  {!isUSD && <p className="text-emerald-600 text-sm font-medium mb-4" data-id="element-414">
                      {currency} · Precio para {country}
                    </p>}
                  {isUSD && <p className="text-slate-500 text-xs mb-4" data-id="element-415">
                      Pago único · Acceso de por vida
                    </p>}

                  {/* Checkmarks */}
                  <div className="grid grid-cols-2 gap-2 mb-6 text-sm" data-id="element-416">
                    {['Acceso inmediato', 'De por vida', 'Garantía 30 días', '100% editable'].map((item, i) => <div key={i} className="flex items-center gap-2 text-slate-700" data-id="element-417">
                        <Check className="w-4 h-4 text-emerald-500" data-id="element-418" />
                        <span data-id="element-419">{item}</span>
                      </div>)}
                  </div>

                  {/* CTA */}
                  <a href={CHECKOUT_URL} className="block w-full bg-orange-500 hover:bg-orange-400 text-white font-black text-xl py-5 px-8 rounded-full shadow-[0_4px_0_rgb(154,52,18)] active:shadow-none active:translate-y-[4px] transition-all text-center uppercase tracking-wide" data-id="element-420">
                    Sí, Quiero el MegaPack 👉
                  </a>

                  {/* Payment logos */}
                  <div className="flex items-center justify-center gap-4 mt-6 opacity-50 grayscale" data-id="element-421">
                    <img src="https://cdn-icons-png.flaticon.com/128/349/349221.png" alt="Visa" className="h-6" data-id="element-422" />
                    <img src="https://cdn-icons-png.flaticon.com/128/349/349228.png" alt="Mastercard" className="h-6" data-id="element-423" />
                    <img src="https://cdn-icons-png.flaticon.com/128/174/174861.png" alt="PayPal" className="h-6" data-id="element-424" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 10: EXPANDED TESTIMONIALS ═══ */}
      <section className="bg-slate-900 py-20 px-4 border-y border-slate-800" data-id="element-425">
        <div className="max-w-6xl mx-auto" data-id="element-426">
          <motion.h2 {...fadeInUp} className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-427">
            Lo que dicen otros docentes
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-428">
            <TestimonialCardDark name="Laura Méndez" role="Docente de Primaria" content="Antes pasaba mis domingos planificando. Con este MegaPack, tengo mis fines de semana de vuelta y mis alumnos están más motivados que nunca." imageInitial="L" color="bg-purple-500" data-id="element-429" />
            <TestimonialCardDark name="Carlos Ruiz" role="Profesor de Secundaria" content="Las evaluaciones A/B son una genialidad. Evito copias y evalúo realmente lo que saben. El material visual ha cambiado mi forma de explicar." imageInitial="C" color="bg-blue-500" data-id="element-430" />
            <TestimonialCardDark name="Ana Torres" role="Maestra de Apoyo" content="Increíble la calidad de los problemas guiados. Ayudan muchísimo a los chicos que les cuesta arrancar. Vale cada centavo." imageInitial="A" color="bg-green-500" data-id="element-431" />
            <TestimonialCardDark name="Patricia Gómez" role="Directora Académica" content="Compramos el pack para todo el departamento de matemática. Unificamos criterios y elevamos el nivel de toda la escuela." imageInitial="P" color="bg-red-500" data-id="element-432" />
            <TestimonialCardDark name="Roberto Díaz" role="Profesor Particular" content="Uso el material para mis clases de apoyo y los padres están encantados con el progreso de sus hijos. Muy recomendado." imageInitial="R" color="bg-orange-500" data-id="element-433" />
            <TestimonialCardDark name="María Fernández" role="Docente Rural" content="En mi escuela rural no teníamos material de calidad. Este MegaPack cambió completamente mis clases. Los chicos ahora aman matemática." imageInitial="M" color="bg-teal-500" data-id="element-434" />
          </div>
        </div>
      </section>

      {/* ═══ SECTION 11: GUARANTEE ═══ */}
      <section className="bg-slate-950 py-20 px-4" data-id="element-435">
        <div className="max-w-3xl mx-auto" data-id="element-436">
          <motion.div {...fadeInUp} className="bg-slate-900 rounded-2xl border border-slate-800 p-8 md:p-12 text-center" data-id="element-437">
            <ShieldCheck className="w-20 h-20 text-emerald-400 mx-auto mb-6" data-id="element-438" />
            <h2 className="text-3xl font-bold mb-4" data-id="element-439">Garantía de 30 Días</h2>
            <p className="text-slate-300 text-lg mb-6" data-id="element-440">
              Prueba el MegaPack sin riesgo. Si el material no cumple tus
              expectativas o no es lo que buscabas, te devolvemos el 100% de tu
              dinero. Sin preguntas, sin complicaciones.
            </p>
            <p className="text-emerald-400 font-bold" data-id="element-441">
              Tu satisfacción está 100% garantizada.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 12: FAQ ═══ */}
      <section className="bg-slate-900 py-20 px-4 border-y border-slate-800" data-id="element-442">
        <div className="max-w-3xl mx-auto" data-id="element-443">
          <motion.h2 {...fadeInUp} className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-444">
            Preguntas Frecuentes
          </motion.h2>

          <motion.div {...fadeInUp} className="space-y-4" data-id="element-445">
            <FAQItem question="¿Para qué grados es el material?" answer="El MegaPack está diseñado para docentes de Primaria y Secundaria. Incluye material adaptable para diferentes niveles y edades." data-id="element-446" />
            <FAQItem question="¿Cómo recibo el material?" answer="Inmediatamente después de tu compra, recibirás un email con acceso a la plataforma donde podrás descargar todo el material en formato PDF y Word editable." data-id="element-447" />
            <FAQItem question="¿El material es editable?" answer="Sí, todo el material viene en formato Word editable para que puedas adaptarlo a tus necesidades específicas." data-id="element-448" />
            <FAQItem question="¿Puedo imprimir el material?" answer="Por supuesto. Todo el material está diseñado para ser impreso. Puedes hacer tantas copias como necesites para tus estudiantes." data-id="element-449" />
            <FAQItem question="¿Hay garantía?" answer="Sí, tienes 30 días de garantía. Si el material no cumple tus expectativas, te devolvemos el 100% de tu dinero sin preguntas." data-id="element-450" />
            <FAQItem question="¿El acceso es de por vida?" answer="Sí, una vez que compras el MegaPack, tienes acceso de por vida al material y todas las actualizaciones futuras." data-id="element-451" />
            <FAQItem question="¿Qué métodos de pago aceptan?" answer="Aceptamos tarjetas de crédito/débito (Visa, Mastercard, American Express), PayPal, y métodos de pago locales según tu país." data-id="element-452" />
          </motion.div>

          <motion.div {...fadeInUp} className="text-center mt-12" data-id="element-453">
            <CTAButton size="lg" data-id="element-454">Quiero el MegaPack Ahora</CTAButton>
          </motion.div>
        </div>
      </section>

      {/* ═══ SECTION 13: FOOTER ═══ */}
      <footer className="bg-slate-950 py-12 px-4 border-t border-slate-800" data-id="element-455">
        <div className="max-w-4xl mx-auto text-center" data-id="element-456">
          <p className="text-slate-500 text-sm mb-4" data-id="element-457">
            © {new Date().getFullYear()} MegaPack Matemática Lista™. Todos los
            derechos reservados.
          </p>
          <p className="text-slate-600 text-xs mb-4" data-id="element-458">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
          <div className="flex justify-center gap-6 text-slate-500 text-xs" data-id="element-459">
            <a href="#" className="hover:text-emerald-400 transition-colors" data-id="element-460">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-emerald-400 transition-colors" data-id="element-461">
              Política de Privacidad
            </a>
            <a href="#" className="hover:text-emerald-400 transition-colors" data-id="element-462">
              Contacto
            </a>
          </div>
        </div>
      </footer>
    </div>;
}